import ScheduledTeacherSelector from '../../../../components/selectors/ScheduledTeacherSelector';
import { Loader2, Download, ChevronLeft, ChevronRight, Filter, User, Check } from 'lucide-react';
import DepartmentTreeSelect from '../selectors/DepartmentTreeSelect';
import React, { useState, useEffect, useCallback } from 'react';
import checkPermission from '../../../../utils/permission';
import { ApiService } from '../../../../services/api';

interface Week {
    id: number;
    name: string;
    value: string;
    startDate: string;
    endDate: string;
    year: number;
    weekNumber: number;
}

interface ReportItem {
    id: string;
    teacher: {
        id: string;
        firstName: string;
        lastName: string;
        fullName: string;
        avatar?: string;
    };
    lesson: {
        id: string;
        title: string;
    };
    department: {
        id: string;
        name: string;
    };
    section: {
        id: string;
        title: string;
    };
    content: {
        id: string;
        title: string;
        type: string;
    };
    week: number;
    year: number;
    weekLabel: string;
    assignedAt: string;
    assignedAtFormatted: string;
    assignmentStatus: 'SENT' | 'NOT_SENT';
    assignmentStatusLabel: string;
    completionPercentage: number | null;
    averageSuccessRate: number | null;
}

interface ReportData {
    data: ReportItem[];
    meta: {
        totalItems: number;
        totalPages: number;
        currentPage: number;
        perPage: number;
    };
}

const contentTypeLabels: { [key: string]: string } = {
    VIDEO: 'Video',
    AUDIO: 'Ses',
    TEXT: 'Metin',
    FILE: 'Dosya',
    CODE: 'Kod',
    TEST: 'Test',
    EXAM: 'Sınav',
};

export default function TeacherHomeworkReportTab() {
    const hasExportPermission = checkPermission('homework_report_list');

    const [reportData, setReportData] = useState<ReportData | null>(null);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [filtered, setFiltered] = useState(false);
    const limit = 20;

    const [teacherDepartmentId, setTeacherDepartmentId] = useState<string | null>(null);
    const [studentDepartmentId, setStudentDepartmentId] = useState<string | null>(null);
    const [loadingFilters, setLoadingFilters] = useState(false);

    const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(null);
    const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>([]);
    const [selectedWeeks, setSelectedWeeks] = useState<number[]>([]);
    const [assignmentStatus, setAssignmentStatus] = useState<string>('');

    const [availableWeeks, setAvailableWeeks] = useState<Week[]>([]);
    const [loadingWeeks, setLoadingWeeks] = useState(false);

    useEffect(() => {
        const loadInitialSettings = async () => {
            setLoadingFilters(true);
            try {
                const educationSettings = await ApiService.getEducationSettings();
                setTeacherDepartmentId(educationSettings?.teacherDepartmentId || null);
                setStudentDepartmentId(educationSettings?.studentDepartmentId || null);
            } catch (err: any) {
                console.error("Error loading filters:", err);
            } finally {
                setLoadingFilters(false);
            }
        };

        loadInitialSettings();
    }, []);

    useEffect(() => {
        const loadWeeks = async () => {
            if (!selectedTeacherId) {
                setAvailableWeeks([]);
                setSelectedWeeks([]);

                return;
            };

            setLoadingWeeks(true);
            try {
                const departmentId = selectedDepartmentIds.length > 0 ? selectedDepartmentIds[0] : undefined;
                const weeks = await ApiService.getTeacherWeeks(selectedTeacherId, departmentId);

                setAvailableWeeks(weeks || []);
                setSelectedWeeks([]);
            } catch (err: any) {
                console.error("Error loading weeks:", err);

                setAvailableWeeks([]);
            } finally {
                setLoadingWeeks(false);
            }
        };

        loadWeeks();
    }, [selectedTeacherId, selectedDepartmentIds]);

    const buildFilter = useCallback(() => {
        const filter: any = {};
        if (selectedTeacherId) filter.teacherId = selectedTeacherId;
        if (selectedDepartmentIds.length > 0) filter.departmentId = selectedDepartmentIds[0];
        if (assignmentStatus) filter.assignmentStatus = assignmentStatus;
        if (selectedWeeks.length > 0) filter.weekNumbers = selectedWeeks;

        return Object.keys(filter).length > 0 ? filter : undefined;
    }, [selectedTeacherId, selectedDepartmentIds, selectedWeeks, assignmentStatus]);

    const loadReport = useCallback(
        async (currentPage: number = 1) => {
            setLoading(true);
            setError(null);
            try {
                const response = await ApiService.getTeacherHomeworkReport({
                    sortBy: "assignedAt",
                    sortOrder: "DESC",
                    page: currentPage,
                    limit,
                    filter: buildFilter(),
                });
                setReportData(response);
                setPage(currentPage);
            } catch (err: any) {
                console.error("Error loading report:", err);
                setError(err?.message || "Rapor yüklenemedi");
                setReportData(null);
            } finally {
                setLoading(false);
            }
        },
        [buildFilter, limit]
    );

    const handleExport = async () => {
        setExporting(true);
        try {
            const blob = await ApiService.exportTeacherHomeworkReport(buildFilter());
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `ogretmen_odev_raporu_${new Date().toISOString().split("T")[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            console.error("Error exporting report:", err);
            setError(err?.message || "Rapor dışa aktarılamadı");
        } finally {
            setExporting(false);
        }
    };

    const isFilterReady = () => {
        return selectedTeacherId && selectedWeeks.length > 0;
    };

    const buildMissingMessage = () => {
        const parts: string[] = [];
        if (!selectedTeacherId) parts.push("Öğretmen");
        if (selectedWeeks.length === 0) parts.push("Hafta");
        if (parts.length === 0) return "";
        if (parts.length === 1) return `${parts[0]} seçiniz.`;
        return `${parts[0]} ve ${parts[1]} seçiniz.`;
    };

    const handleFilter = () => {
        if (isFilterReady()) {
            setFiltered(true);
            loadReport(1);
        }
    };

    const handleClearFilters = () => {
        setSelectedTeacherId(null);
        setSelectedDepartmentIds([]);
        setSelectedWeeks([]);
        setAvailableWeeks([]);
        setAssignmentStatus('');
        setReportData(null);
        setError(null);
        setFiltered(false);
    };

    const handleTeacherChange = (userId: string) => {
        setSelectedTeacherId(userId || null);

        setSelectedDepartmentIds([]);
        setSelectedWeeks([]);
        setAvailableWeeks([]);
    };

    const handleDepartmentChange = (deptIds: string[]) => {
        setSelectedDepartmentIds(deptIds);

        setSelectedWeeks([]);
    };

    return (
        <div className="h-full flex flex-col">
            {!filtered && (
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6 overflow-visible flex-shrink-0">
                    <div className="flex items-center gap-3 mb-4">
                        <Filter className="w-5 h-5 text-gray-600" />
                        <h3 className="text-lg font-semibold text-gray-900">Filtreler</h3>
                    </div>

                    {loadingFilters ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                            <span className="ml-2 text-gray-600">Filtreler yükleniyor...</span>
                        </div>
                    ) : (
                        <div className="overflow-visible">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 overflow-visible">
                                <div className="col-span-1">
                                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">Öğretmen</label>
                                    <ScheduledTeacherSelector
                                        value={selectedTeacherId}
                                        onChange={handleTeacherChange}
                                        placeholder="Öğretmen seçin..."
                                        allowClear={true}
                                        className="w-full"
                                        source="report"
                                    />
                                </div>

                                <div className="col-span-1">
                                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">Sınıf</label>
                                    <DepartmentTreeSelect
                                        value={selectedDepartmentIds}
                                        onChange={handleDepartmentChange}
                                        multiple={false}
                                        teacherId={selectedTeacherId}
                                        placeholder="Tümü"
                                    />
                                </div>

                                <div className="col-span-1">
                                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                                        Hafta {selectedWeeks.length > 0 && `(${selectedWeeks.length} Seçili)`}
                                    </label>
                                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                                        <div className="max-h-64 overflow-auto divide-y divide-gray-200">
                                            {loadingWeeks ? (
                                                <div className="px-3 py-2 text-sm text-gray-500 flex items-center justify-center">
                                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                                    Yükleniyor...
                                                </div>
                                            ) : !selectedTeacherId ? (
                                                <div className="px-3 py-2 text-sm text-gray-500">Önce öğretmen seçiniz.</div>
                                            ) : availableWeeks.length === 0 ? (
                                                <div className="px-3 py-2 text-sm text-gray-500">Hafta bulunamadı.</div>
                                            ) : (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedWeeks([])}
                                                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${selectedWeeks.length === 0 ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-600"
                                                            }`}
                                                        disabled={!selectedTeacherId}
                                                    >
                                                        Hafta Seçiniz
                                                    </button>
                                                    {availableWeeks.map((week) => {
                                                        const isSelected = selectedWeeks.includes(week.id);
                                                        return (
                                                            <button
                                                                key={`${week.year}-${week.id}`}
                                                                type="button"
                                                                onClick={() => {
                                                                    const weekSet = new Set(selectedWeeks);
                                                                    if (weekSet.has(week.id)) {
                                                                        weekSet.delete(week.id);
                                                                    } else {
                                                                        weekSet.add(week.id);
                                                                    }
                                                                    setSelectedWeeks(Array.from(weekSet).sort((a, b) => a - b));
                                                                }}
                                                                className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between ${isSelected ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50 text-gray-800"
                                                                    }`}
                                                                disabled={!selectedTeacherId}
                                                            >
                                                                <span>
                                                                    <span className="font-medium">{week.name}</span>
                                                                    <span className="text-xs text-gray-500 ml-2">({week.value})</span>
                                                                </span>
                                                                {isSelected && <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                                                            </button>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-span-1">
                                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">Ödev Gönderilme Durumu</label>
                                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                                        <div className="divide-y divide-gray-200">
                                            <button
                                                type="button"
                                                onClick={() => setAssignmentStatus('')}
                                                className={`w-full text-left px-3 py-2 text-sm transition-colors ${assignmentStatus === '' ? 'bg-blue-50 text-blue-900 font-medium' : 'hover:bg-gray-50 text-gray-800'
                                                    }`}
                                            >
                                                Tümü
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setAssignmentStatus('SENT')}
                                                className={`w-full text-left px-3 py-2 text-sm transition-colors ${assignmentStatus === 'SENT' ? 'bg-blue-50 text-blue-900 font-medium' : 'hover:bg-gray-50 text-gray-800'
                                                    }`}
                                            >
                                                Gönderildi
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setAssignmentStatus('NOT_SENT')}
                                                className={`w-full text-left px-3 py-2 text-sm transition-colors ${assignmentStatus === 'NOT_SENT' ? 'bg-blue-50 text-blue-900 font-medium' : 'hover:bg-gray-50 text-gray-800'
                                                    }`}
                                            >
                                                Gönderilmedi
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 mt-4">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={handleFilter}
                                disabled={!isFilterReady()}
                                className={`px-6 py-2 rounded-lg font-medium text-sm transition-colors ${isFilterReady() ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    }`}
                            >
                                Filtrele
                            </button>
                            {!isFilterReady() && (
                                <span className="text-xs text-gray-600">Raporları görüntülemek için lütfen {buildMissingMessage()}</span>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {filtered && (
                <div className="grid grid-cols-1 md:grid-cols-3 items-center mb-6 gap-4">
                    <div className="flex items-center space-x-4 justify-start">
                        <div className="flex flex-row flex-wrap gap-2">
                            <button
                                onClick={handleClearFilters}
                                className="px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium border flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2 bg-white text-gray-700 hover:bg-gray-50 border-gray-300 transition-colors"
                            >
                                <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                <span>Filtrele</span>
                            </button>
                            {/* {reportData && hasExportPermission && (
                                <button
                                    onClick={handleExport}
                                    disabled={exporting}
                                    className="px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium bg-orange-600 text-white hover:bg-orange-700 border border-orange-600 flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                >
                                    {exporting ? <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" /> : <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                                    <span>{exporting ? "İndiriliyor..." : "Dışa Aktar"}</span>
                                </button>
                            )} */}
                        </div>
                    </div>
                    <div />
                    {reportData && (
                        <div className="flex items-center space-x-4 justify-start md:justify-end">
                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                                <span>
                                    {reportData.meta.totalItems > 0
                                        ? `${(page - 1) * limit + 1}-${Math.min(page * limit, reportData.meta.totalItems)} / ${reportData.meta.totalItems}`
                                        : "0 / 0"}
                                </span>
                                <button onClick={() => loadReport(page - 1)} disabled={page <= 1 || loading} className="disabled:opacity-50 disabled:cursor-not-allowed">
                                    <ChevronLeft className="h-4 w-4 text-gray-400" />
                                </button>
                                <button
                                    onClick={() => loadReport(page + 1)}
                                    disabled={page >= reportData.meta.totalPages || loading}
                                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronRight className="h-4 w-4 text-gray-400" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {filtered && error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <p className="text-sm font-medium text-red-900">Hata</p>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
            )}

            <div className="flex-1 min-h-0 overflow-auto">
                {!filtered ? (
                    <div className="flex-1 flex items-center justify-center h-full"></div>
                ) : loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                        <span className="ml-3 text-gray-600">Rapor yükleniyor...</span>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Hata Oluştu</h3>
                        <p className="text-gray-500 max-w-md mb-4">{error}</p>
                        <button
                            onClick={() => {
                                setError(null);
                                handleFilter();
                            }}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Tekrar Dene
                        </button>
                    </div>
                ) : reportData && reportData.data.length > 0 ? (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="block md:hidden divide-y divide-gray-200">
                            {reportData.data.map((row) => (
                                <div key={row.id} className="p-4 space-y-3">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <User className="w-4 h-4 text-blue-600" />
                                                <p className="font-semibold text-gray-900">{row.teacher.fullName}</p>
                                            </div>
                                            <p className="text-sm text-gray-600">{row.lesson.title}</p>
                                        </div>
                                        <div className="flex flex-col items-end gap-1">
                                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">{row.weekLabel}</span>
                                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${row.assignmentStatus === 'SENT' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                }`}>
                                                {row.assignmentStatusLabel}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div>
                                            <span className="text-gray-500">Sınıf:</span>
                                            <span className="ml-1 text-gray-900">{row.department.name}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">İçerik Türü:</span>
                                            <span className="ml-1 text-gray-900">{contentTypeLabels[row.content.type] || row.content.type}</span>
                                        </div>
                                    </div>
                                    <div className="text-sm">
                                        <span className="text-gray-500">Konu:</span>
                                        <span className="ml-1 text-gray-900">{row.section.title}</span>
                                    </div>
                                    <div className="text-sm">
                                        <span className="text-gray-500">İçerik:</span>
                                        <span className="ml-1 text-gray-900">{row.content.title}</span>
                                    </div>
                                    <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">{row.assignedAtFormatted}</div>
                                    <div className="flex items-center gap-4 mt-2 text-xs">
                                        <div className="flex items-center gap-1">
                                            <span className="text-gray-500">Yapılma Oranı:</span>
                                            <span className="font-semibold text-gray-900">
                                                {row.completionPercentage !== null ? `%${row.completionPercentage}` : "-"}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <span className="text-gray-500">Ortalama Puan:</span>
                                            <span className="font-semibold text-gray-900">
                                                {row.averageSuccessRate !== null ? (
                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] ${row.averageSuccessRate >= 70 ? "bg-green-100 text-green-800" :
                                                        row.averageSuccessRate >= 50 ? "bg-yellow-100 text-yellow-800" :
                                                            "bg-red-100 text-red-800"
                                                        }`}>
                                                        %{row.averageSuccessRate}
                                                    </span>
                                                ) : "-"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="hidden md:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 text-xs">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Öğretmen</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ders</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sınıf</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Konu</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">İçerik</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tür</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hafta</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durum</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarih</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Yapılma Oranı</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ortalama Puan</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {reportData.data.map((row, idx) => (
                                        <tr key={row.id} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="h-6 w-6 flex-shrink-0">
                                                        {row.teacher.avatar ? (
                                                            <img className="h-6 w-6 rounded-full object-cover" src={row.teacher.avatar} alt="" />
                                                        ) : (
                                                            <div className="h-6 w-6 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                                                                <span className="text-white text-[10px] font-medium">
                                                                    {row.teacher.firstName?.[0]}
                                                                    {row.teacher.lastName?.[0]}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="ml-2">
                                                        <div className="text-xs font-medium text-gray-900">{row.teacher.fullName}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{row.lesson.title}</td>
                                            <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{row.department.name}</td>
                                            <td className="px-3 py-2 text-xs text-gray-900 max-w-[120px] truncate">{row.section.title}</td>
                                            <td className="px-3 py-2 text-xs text-gray-900 max-w-[120px] truncate">{row.content.title}</td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 text-gray-800">
                                                    {contentTypeLabels[row.content.type] || row.content.type}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-blue-100 text-blue-800">{row.weekLabel}</span>
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${row.assignmentStatus === 'SENT' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                    }`}>
                                                    {row.assignmentStatusLabel}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">{row.assignedAtFormatted}</td>
                                            <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900 font-medium">
                                                {row.completionPercentage !== null ? (
                                                    <span className={`px-2 py-1 rounded-full text-[10px] ${row.completionPercentage >= 50 ? "bg-green-100 text-green-800" : "bg-orange-100 text-orange-800"
                                                        }`}>
                                                        %{row.completionPercentage}
                                                    </span>
                                                ) : "-"}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900 font-medium">
                                                {row.averageSuccessRate !== null ? (
                                                    <span className={`px-2 py-1 rounded-full text-[10px] ${row.averageSuccessRate >= 70 ? "bg-green-100 text-green-800" :
                                                        row.averageSuccessRate >= 50 ? "bg-yellow-100 text-yellow-800" :
                                                            "bg-red-100 text-red-800"
                                                        }`}>
                                                        %{row.averageSuccessRate}
                                                    </span>
                                                ) : "-"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Veri Bulunamadı</h3>
                        <p className="text-gray-500 max-w-md mb-4">Seçilen kriterlere uygun rapor verisi bulunamadı.</p>
                        <button onClick={handleFilter} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                            Yenile
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
